"use client";

import { RefreshCw, WifiOff } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";

import { useCurrentUser } from "@/hooks/use-current-user";
import { startNativePush } from "@/lib/native/push";
import {
  isExternalWebUrl,
  isNativeApp,
  nativeUrlToAppPath,
} from "@/lib/native/platform";

export function NativeRuntime({ children }: { children: ReactNode }) {
  const router = useRouter();
  const currentUserQuery = useCurrentUser();
  const [offline, setOffline] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }

    let cancelled = false;
    const cleanups: Array<() => void> = [];

    async function start() {
      const [
        { App },
        { Browser },
        { Capacitor },
        { Keyboard, KeyboardResize },
        { Network },
        { SplashScreen },
        { StatusBar, Style },
      ] = await Promise.all([
        import("@capacitor/app"),
        import("@capacitor/browser"),
        import("@capacitor/core"),
        import("@capacitor/keyboard"),
        import("@capacitor/network"),
        import("@capacitor/splash-screen"),
        import("@capacitor/status-bar"),
      ]);

      if (cancelled) {
        return;
      }

      await Promise.allSettled([
        StatusBar.setOverlaysWebView({ overlay: false }),
        StatusBar.setStyle({ style: Style.Light }),
        Capacitor.getPlatform() === "android"
          ? StatusBar.setBackgroundColor({ color: "#ffffff" })
          : Promise.resolve(),
        Keyboard.setResizeMode({ mode: KeyboardResize.Body }),
      ]);

      const openAppUrl = (rawUrl: string) => {
        const path = nativeUrlToAppPath(rawUrl);
        if (path) {
          router.push(path);
        }
      };

      const launch = await App.getLaunchUrl().catch(() => undefined);
      if (launch?.url) {
        openAppUrl(launch.url);
      }

      const urlListener = await App.addListener("appUrlOpen", ({ url }) => {
        openAppUrl(url);
      });
      cleanups.push(() => void urlListener.remove());

      const backListener = await App.addListener(
        "backButton",
        async ({ canGoBack }) => {
          if (canGoBack) {
            window.history.back();
            return;
          }
          await App.minimizeApp();
        },
      );
      cleanups.push(() => void backListener.remove());

      const initialNetwork = await Network.getStatus();
      setOffline(!initialNetwork.connected);
      const networkListener = await Network.addListener(
        "networkStatusChange",
        ({ connected }) => setOffline(!connected),
      );
      cleanups.push(() => void networkListener.remove());

      const handleExternalLink = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }
        const anchor = target.closest("a[href]");
        if (!(anchor instanceof HTMLAnchorElement)) {
          return;
        }
        if (!isExternalWebUrl(anchor.href)) {
          return;
        }
        event.preventDefault();
        void Browser.open({
          url: anchor.href,
          presentationStyle: "popover",
        });
      };
      document.addEventListener("click", handleExternalLink, true);
      cleanups.push(() =>
        document.removeEventListener("click", handleExternalLink, true),
      );

      await SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => undefined);
    }

    void start().catch((error) => {
      console.error("A natív alkalmazáskörnyezet nem indítható el.", error);
    });

    return () => {
      cancelled = true;
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [router]);

  useEffect(() => {
    if (!isNativeApp()) {
      return;
    }
    if (currentUserQuery.isLoading || !currentUserQuery.data) {
      return;
    }
    if (currentUserQuery.data.accountStatus !== "active") {
      return;
    }

    const cleanups: Array<() => void> = [];
    let cancelled = false;

    void startNativePush({
      router,
      onCleanup: (cleanup) => {
        if (cancelled) {
          cleanup();
          return;
        }
        cleanups.push(cleanup);
      },
    }).catch((error) => {
      console.error("A push értesítések nem indíthatók el.", error);
    });

    return () => {
      cancelled = true;
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [router, currentUserQuery.isLoading, currentUserQuery.data]);

  async function retryConnection() {
    setRetrying(true);
    try {
      const { Network } = await import("@capacitor/network");
      const status = await Network.getStatus();
      setOffline(!status.connected);
      if (status.connected) {
        window.location.reload();
      }
    } finally {
      setRetrying(false);
    }
  }

  return (
    <>
      {children}
      {offline ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background px-6 text-center">
          <section className="w-full max-w-sm rounded-3xl border bg-card p-7 shadow-xl">
            <span className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <WifiOff className="size-7" />
            </span>
            <h1 className="mt-5 text-xl font-semibold">Nincs internetkapcsolat</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              A PraxisTárs használatához internetkapcsolat szükséges. Ellenőrizze
              a Wi-Fi- vagy mobilhálózatot, majd próbálja újra.
            </p>
            <button
              type="button"
              className="mt-6 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"
              disabled={retrying}
              onClick={() => void retryConnection()}
            >
              <RefreshCw className={retrying ? "size-4 animate-spin" : "size-4"} />
              Újrapróbálás
            </button>
          </section>
        </div>
      ) : null}
    </>
  );
}
